import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Eye, Mail } from "lucide-react";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background animate-fade-in" data-testid="privacy-page">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 py-16">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-zinc-400 hover:text-white transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight" data-testid="page-title">
            Privacy Policy
          </h1>
          <p className="text-zinc-400 mt-4">Last updated: January 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Privacy Highlights */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
              <Shield className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-white font-bold">Data Protected</p>
              <p className="text-zinc-400 text-sm">256-bit encryption</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
              <Lock className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-white font-bold">Never Sold</p>
              <p className="text-zinc-400 text-sm">Your data stays private</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
              <Mail className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-white font-bold">Easy Opt-Out</p>
              <p className="text-zinc-400 text-sm">Unsubscribe anytime</p>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-invert max-w-none space-y-8">
            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Introduction</h2>
              <p className="text-zinc-400">
                At Brendolly's Auto Parts ("we," "us," or "our"), we are committed to protecting your privacy and ensuring 
                the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and 
                safeguard your information when you visit our website brendollysautoparts.com or make a purchase.
              </p>
              <p className="text-zinc-400 mt-4">
                By using our Site, you consent to the data practices described in this policy.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-white mt-6 mb-3">Personal Information</h3>
              <p className="text-zinc-400 mb-3">When you create an account or place an order, we collect:</p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Full name</li>
                <li>Email address</li>
                <li>Shipping and billing address</li>
                <li>Phone number</li>
                <li>Payment information (processed securely via third-party payment processors)</li>
              </ul>

              <h3 className="text-lg font-semibold text-white mt-6 mb-3">Automatically Collected Information</h3>
              <p className="text-zinc-400 mb-3">When you browse our Site, we automatically collect:</p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Pages visited and time spent</li>
                <li>Referring website</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">How We Use Your Information</h2>
              <p className="text-zinc-400 mb-4">We use the information we collect to:</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-zinc-950 p-4 border border-zinc-700">
                  <h4 className="text-primary font-semibold mb-2">Order Processing</h4>
                  <ul className="text-zinc-400 text-sm space-y-1">
                    <li>• Process and fulfill your orders</li>
                    <li>• Send order confirmations</li>
                    <li>• Provide shipping updates</li>
                    <li>• Process returns and refunds</li>
                  </ul>
                </div>
                <div className="bg-zinc-950 p-4 border border-zinc-700">
                  <h4 className="text-primary font-semibold mb-2">Customer Service</h4>
                  <ul className="text-zinc-400 text-sm space-y-1">
                    <li>• Respond to inquiries</li>
                    <li>• Provide technical support</li>
                    <li>• Handle complaints</li>
                    <li>• Improve our services</li>
                  </ul>
                </div>
                <div className="bg-zinc-950 p-4 border border-zinc-700">
                  <h4 className="text-primary font-semibold mb-2">Marketing (Opt-in)</h4>
                  <ul className="text-zinc-400 text-sm space-y-1">
                    <li>• Send promotional emails</li>
                    <li>• Notify of sales and deals</li>
                    <li>• Product recommendations</li>
                    <li>• Newsletter updates</li>
                  </ul>
                </div>
                <div className="bg-zinc-950 p-4 border border-zinc-700">
                  <h4 className="text-primary font-semibold mb-2">Site Improvement</h4>
                  <ul className="text-zinc-400 text-sm space-y-1">
                    <li>• Analyze usage patterns</li>
                    <li>• Improve user experience</li>
                    <li>• Troubleshoot issues</li>
                    <li>• Prevent fraud</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Information Sharing</h2>
              <div className="bg-primary/10 border border-primary p-4 mb-6">
                <p className="text-white font-semibold">
                  We do NOT sell, trade, or rent your personal information to third parties.
                </p>
              </div>
              <p className="text-zinc-400 mb-4">We may share your information only with:</p>
              <ul className="text-zinc-400 space-y-3">
                <li>
                  <strong className="text-white">Shipping Partners:</strong> To deliver your orders (e.g., UPS, FedEx, USPS). 
                  They receive only the information necessary for delivery.
                </li>
                <li>
                  <strong className="text-white">Payment Processors:</strong> To process transactions securely (e.g., Stripe, PayPal). 
                  We do not store your full credit card information.
                </li>
                <li>
                  <strong className="text-white">Legal Requirements:</strong> If required by law, court order, or government request.
                </li>
                <li>
                  <strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.
                </li>
              </ul>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Data Security</h2>
              <div className="flex items-start gap-4">
                <Lock className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                <div>
                  <p className="text-zinc-400 mb-4">
                    We implement industry-standard security measures to protect your personal information:
                  </p>
                  <ul className="list-disc list-inside text-zinc-400 space-y-2">
                    <li>SSL/TLS encryption for all data transmission</li>
                    <li>PCI DSS compliance for payment processing</li>
                    <li>Regular security audits and updates</li>
                    <li>Secure data centers with limited access</li>
                    <li>Employee training on data protection</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Cookies</h2>
              <p className="text-zinc-400 mb-4">
                We use cookies and similar technologies to enhance your browsing experience. Cookies are small files 
                stored on your device that help us:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Remember your preferences and cart items</li>
                <li>Understand how you use our Site</li>
                <li>Improve our services</li>
                <li>Provide personalized content</li>
              </ul>
              <p className="text-zinc-400 mt-4">
                You can manage cookie preferences through your browser settings. Note that disabling cookies may 
                affect Site functionality.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Your Rights & Choices</h2>
              <div className="space-y-4">
                <div className="bg-zinc-950 p-4 border-l-4 border-primary">
                  <h4 className="text-white font-semibold">Marketing Opt-Out</h4>
                  <p className="text-zinc-400 mt-1">
                    You can unsubscribe from marketing emails at any time by clicking the "Unsubscribe" link in any 
                    email or by contacting us. Note: You will still receive transactional emails (order confirmations, 
                    shipping updates).
                  </p>
                </div>
                <div className="bg-zinc-950 p-4 border-l-4 border-primary">
                  <h4 className="text-white font-semibold">Access & Correction</h4>
                  <p className="text-zinc-400 mt-1">
                    You can access and update your personal information by logging into your account or contacting us.
                  </p>
                </div>
                <div className="bg-zinc-950 p-4 border-l-4 border-primary">
                  <h4 className="text-white font-semibold">Data Deletion</h4>
                  <p className="text-zinc-400 mt-1">
                    You may request deletion of your personal data by contacting us. Note: We may retain certain 
                    information as required by law or for legitimate business purposes.
                  </p>
                </div>
              </div>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Children's Privacy</h2>
              <p className="text-zinc-400">
                Our Site is not intended for children under 18 years of age. We do not knowingly collect personal 
                information from children. If you believe we have collected information from a child, please contact 
                us immediately.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Changes to This Policy</h2>
              <p className="text-zinc-400">
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an 
                updated "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Contact Us</h2>
              <p className="text-zinc-400 mb-4">
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-zinc-950 p-4 border border-zinc-700">
                <p className="text-white font-semibold">Brendolly's Auto Parts - Privacy Team</p>
                <p className="text-zinc-400">Email: privacy@brendollysautoparts.com</p>
                <p className="text-zinc-400">Phone: 781-870-7976</p>
                <p className="text-zinc-400 mt-2">72 Chelsea Street</p>
                <p className="text-zinc-400">Everett, MA 02149</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
